import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import ReactMarkdown from 'react-markdown';
import { openProductSearch } from './product-detail/navigation';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { fetchIndustryCollection, fetchLatestPosts, fetchPost, postImage, postParagraphs, postSummary, postTags, safeWebUrl, type CmsPost, type IndustryCollection } from '../services/bakeryHubService';
import './BakeryHub.css';

const assets = '/assets/bakery-hub/';
const categories = [
  { name: 'Bread', image: 'bread.png', description: 'Techniques, ingredients and market trends' },
  { name: 'Fast Food', image: 'fast-food.png', description: 'Solutions for on-the-go and out-of-home' },
  { name: 'Sweets', image: 'sweets.png', description: 'Indulgence, seasonality and consumer inspiration' },
];
function formatDate(value?: string | null) {
  if (!value || Number.isNaN(Date.parse(value))) return '';
  return new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function BakeryHub() {
  const { user } = useAuth0();
  const [posts, setPosts] = useState<CmsPost[]>([]);
  const [industry, setIndustry] = useState<IndustryCollection>();
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingIndustry, setLoadingIndustry] = useState(true);
  const [postsError, setPostsError] = useState(false);
  const [industryError, setIndustryError] = useState(false);
  const [reload, setReload] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState<CmsPost>();
  const [postDetail, setPostDetail] = useState<CmsPost>();
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [detailReload, setDetailReload] = useState(0);
  const [bookmarks, setBookmarks] = useState<number[]>([]);

  useEffect(() => {
    setPostDetail(undefined);
    setDetailError(false);
    if (!selectedPost) { setDetailLoading(false); return; }
    const controller = new AbortController();
    setDetailLoading(true);
    void fetchPost(selectedPost.id, controller.signal).then(detail => {
      if (!controller.signal.aborted) setPostDetail(detail);
    }).catch(() => {
      if (!controller.signal.aborted) setDetailError(true);
    }).finally(() => { if (!controller.signal.aborted) setDetailLoading(false); });
    return () => controller.abort();
  }, [selectedPost, detailReload]);

  useEffect(() => {
    const controller = new AbortController();
    setLoadingPosts(true); setLoadingIndustry(true); setPostsError(false); setIndustryError(false);
    void fetchLatestPosts(controller.signal).then(setPosts).catch(() => {
      if (!controller.signal.aborted) setPostsError(true);
    }).finally(() => { if (!controller.signal.aborted) setLoadingPosts(false); });
    void fetchIndustryCollection(controller.signal).then(collection => { setIndustry(collection); setPage(1); }).catch(() => {
      if (!controller.signal.aborted) setIndustryError(true);
    }).finally(() => { if (!controller.signal.aborted) setLoadingIndustry(false); });
    return () => controller.abort();
  }, [reload]);

  const [featured, ...secondary] = posts;
  const links = industry?.links ?? [];
  const pages = Math.max(1, Math.ceil(links.length / 3));
  const displayName = user?.given_name || user?.name?.split(' ')[0] || 'there';

  function article(post: CmsPost, isFeatured = false) {
    const image = postImage(post);
    return <article key={post.id} className={`hub-article ${isFeatured ? 'hub-featured' : 'hub-secondary'}`}>
      <button className="hub-article-link" onClick={() => setSelectedPost(post)} aria-label={`Read ${post.title}`}>
        <div className={`hub-visual ${image ? '' : 'hub-no-image'}`}>
          {image && <img src={image} decoding="async" loading={isFeatured ? "eager" : "lazy"} fetchPriority={isFeatured ? "high" : "auto"} alt={typeof post.image === 'object' ? post.image.alt || '' : ''} />}
          <div className="hub-tags">{postTags(post).map(tag => <span key={tag} className={`hub-tag ${tag.toLowerCase() === 'digital' ? 'hub-tag-digital' : ''}`}>{tag}</span>)}</div>
        </div>
        <p className="hub-meta">Our blog{formatDate(post.publishedAt || post.createdAt) && ` · ${formatDate(post.publishedAt || post.createdAt)}`}</p>
        <h2>{post.title}</h2>
        <p className="hub-summary">{postSummary(post)}</p>
      </button>
    </article>;
  }

  return <div className="bakery-hub">
    <header className="hub-welcome">
      <div><p className="hub-greeting">Welcome back, {displayName} <span aria-hidden="true"> | </span> {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
        <h1>Fresh thinking.<br />Shared globally.</h1></div>
      <p className="hub-intro">Insights, ideas and expertise from across our bakery business and beyond.</p>
    </header>
    <nav className="hub-categories" aria-label="Product categories">
      {categories.map(item => <button key={item.name} className="hub-category" aria-label={`Browse ${item.name} products`} onClick={() => openProductSearch("", item.name)}>
        <img className="hub-category-photo" src={`${assets}${item.image}`} alt="" />
        <span className="hub-category-copy"><strong>{item.name}</strong><span>{item.description}</span></span>
        <img className="hub-arrow" src={`${assets}arrow.svg`} alt="" />
      </button>)}
    </nav>
    <div className="hub-editorial">
      <section className="hub-blog" aria-label="Latest blog articles" aria-busy={loadingPosts}>
        {loadingPosts ? <p className="hub-state" role="status">Loading the latest articles…</p> : postsError ? <div className="hub-state" role="alert"><p>We couldn’t load the latest articles.</p><Button variant="outline" onClick={() => setReload(value => value + 1)}>Try again</Button></div> : featured ? <>{article(featured, true)}<div className="hub-secondary-list">{secondary.map(post => article(post))}</div></> : <p className="hub-state">New articles are coming soon.</p>}
      </section>
      <aside className="hub-industry" aria-label="From the industry" aria-busy={loadingIndustry}>
        <div className="hub-industry-heading"><h2>{industry?.name || 'From the industry'}</h2><p className="hub-industry-intro">{industry?.slogan || 'Curated external insights from around the world.'}</p></div>
        <div className="hub-industry-links">
          {loadingIndustry ? <p role="status">Loading industry insights…</p> : industryError ? <div role="alert"><p>Industry insights are currently unavailable.</p><Button variant="outline" onClick={() => setReload(value => value + 1)}>Try again</Button></div> : links.length ? links.slice((page - 1) * 3, page * 3).map(link => {
            const url = safeWebUrl(link.url);
            return <article className="hub-news" key={link.id}>
              <p className="hub-news-meta">{typeof link.tag === 'object' ? link.tag.name : 'Industry'}{formatDate(link.date) && ` · ${formatDate(link.date)}`}</p>
              <h3>{url ? <a href={url} target="_blank" rel="noopener noreferrer">{link.title}<img src={`${assets}external.svg`} alt="Opens in a new tab" /></a> : link.title}</h3>
              <p>{link.description}</p>
            </article>;
          }) : <p>No industry insights have been added yet.</p>}
        </div>
        {!loadingIndustry && !industryError && links.length > 0 && <nav className="hub-pagination" aria-label="Industry pages">
          <button disabled={page === 1} onClick={() => setPage(value => value - 1)}>Previous <img src={`${assets}chevron-left.svg`} alt="" /></button>
          <div>{Array.from({ length: pages }, (_, i) => i + 1).map(number => <button key={number} aria-label={`Industry page ${number}`} aria-current={page === number ? 'page' : undefined} onClick={() => setPage(number)}>{number}</button>)}</div>
          <button disabled={page === pages} onClick={() => setPage(value => value + 1)}><img src={`${assets}chevron-right.svg`} alt="" /> Next</button>
        </nav>}
      </aside>
    </div>
    <Dialog open={!!selectedPost} onOpenChange={open => { if (!open) setSelectedPost(undefined); }}>
      {selectedPost && <DialogContent className="hub-reader" closeIcon={<img src="/assets/blog-reader/close.svg" alt="" />}>
        <header className="hub-reader-header">
          <div className="hub-reader-topbar">
            <div className="hub-reader-meta">
              {postTags(selectedPost).slice(0, 1).map(tag => <span className="hub-reader-tag" key={tag}>{tag}</span>)}
              {postDetail && <span>{postTags(selectedPost).length > 0 && '· '}{Math.max(1, Math.ceil((postDetail.contentMarkdown || postParagraphs(postDetail).join(' ')).split(/\s+/).filter(Boolean).length / 200))} min read</span>}
            </div>
            <Button variant="ghost" size="icon" className="hub-reader-bookmark" aria-label={bookmarks.includes(selectedPost.id) ? 'Remove bookmark' : 'Bookmark article for this session'} aria-pressed={bookmarks.includes(selectedPost.id)} onClick={() => setBookmarks(ids => ids.includes(selectedPost.id) ? ids.filter(id => id !== selectedPost.id) : [...ids, selectedPost.id])}>
              <img src="/assets/blog-reader/bookmark.svg" alt="" />
            </Button>
          </div>
          <DialogTitle className="hub-reader-title">{selectedPost.title}</DialogTitle>
          <DialogDescription className="hub-reader-description">Our blog{formatDate(selectedPost.publishedAt || selectedPost.createdAt) && ` · ${formatDate(selectedPost.publishedAt || selectedPost.createdAt)}`}</DialogDescription>
        </header>
        <div className="hub-reader-viewport" tabIndex={0} role="region" aria-label="Article content">
        {postImage(selectedPost) && <img className="hub-reader-image" src={postImage(selectedPost)} alt={typeof selectedPost.image === 'object' ? selectedPost.image.alt || '' : ''} />}
        <div className="hub-reader-body">{detailLoading ? <p role="status">Loading article…</p> : detailError ? <div role="alert"><p>We couldn’t load this article.</p><Button variant="outline" onClick={() => setDetailReload(value => value + 1)}>Try again</Button></div> : postDetail?.contentMarkdown ? <ReactMarkdown>{postDetail.contentMarkdown}</ReactMarkdown> : postDetail && postParagraphs(postDetail).map((text, index) => <p key={index}>{text}</p>)}</div>
        </div>
        <footer className="hub-reader-footer"><DialogClose asChild><Button size="sm">Close Reader</Button></DialogClose></footer>
      </DialogContent>}
    </Dialog>
  </div>;
}
